import { useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Icon } from "../kit";
import { EASE_OUT } from "../theme";
import { useRing } from "./ringContext";
import { ringLabel, useRingSet, type RingRow } from "./RingManagerModal";
import type {
  Catalogs, DiamondClarity, DiamondConfig, GiaColorGrade, HeadUnderstructure, RingState,
} from "../../shared/api-contract";

/**
 * The Ring Builder's pinned footer: the live read-only spec (band figures) plus
 * the centre-stone readout and the "setting only" price estimate (engagement
 * figures), with the manufacturability warnings above it.
 *
 * Everything here is READ-ONLY — no RPC is called, so there is nothing to caps-
 * gate; `RingProvider` already shows its fallback when a pre-v37 viewer rejects
 * `getRingState`.
 */

// ─── Pricing (display-config constants, ported from ui/engagement/priceConfig) ──
//
// A client-side ESTIMATE, not a quote. Rates are static constants and every
// surface that shows the number says "Live estimate".

export const METAL_RATE_PER_GRAM_USD: Record<"14k" | "18k" | "platinum", number> = {
  "14k": 55,
  "18k": 75,
  platinum: 40,
};

export const PAVE_RATE_PER_CT_USD = 450;
export const LABOR_BASE_USD = 350;
export const LABOR_PER_STONE_USD = 12;
export const RETAIL_MARGIN = 1.75;

export const HEAD_COMPLEXITY_BUMP_USD: Partial<Record<HeadUnderstructure, number>> = {
  halo: 90,
  "hidden-halo": 90,
  "double-halo": 90,
  bezel: 60,
};

export const MULTI_STONE_SIDE_BUMP_USD = 120;

// ─── Diamond specification factors (the v43 Custom Settings) ────────────────
//
// COMMERCIAL ASSUMPTIONS, not measured from the reference: its Custom Settings
// panel moves a live quote but never exposes the arithmetic behind it. These are
// multipliers on the melee $/ct only — the centre stone is not priced here at
// all — and 1.0 is the grade `PAVE_RATE_PER_CT_USD` is quoted at: NATURAL, G,
// VS. Unset colour/clarity therefore price exactly as they did before v43.

export const LAB_GROWN_MELEE_FACTOR = 0.35;

export const COLOR_GRADE_FACTOR: Record<GiaColorGrade, number> = {
  D: 1.3, E: 1.22, F: 1.14, G: 1.0, H: 0.92, I: 0.84, J: 0.76, K: 0.68,
};

export const CLARITY_FACTOR: Record<DiamondClarity, number> = {
  FL: 1.45, IF: 1.32, VVS: 1.16, VS: 1.0, SI: 0.86, I: 0.7,
};

/** Melee $/ct multiplier for a run's origin + colour + clarity. 1.0 when the
 *  design specifies none of them. */
export function meleeQualityFactor(d: DiamondConfig): number {
  return (
    (d.origin === "lab" ? LAB_GROWN_MELEE_FACTOR : 1) *
    (d.colorGrade ? COLOR_GRADE_FACTOR[d.colorGrade] : 1) *
    (d.clarity ? CLARITY_FACTOR[d.clarity] : 1)
  );
}

/**
 * $/g for a `BandState.metalKarat` id (`KARAT_OPTIONS`: gold10k/gold14k/gold18k/
 * platinum950/silver925/palladium950). The merged spec has a single scalar
 * `weightGrams` computed from the karat DENSITY, so the karat — not the metal
 * preset — is the right rate key here (the engagement estimator had no karat
 * field and billed every gold at 18K). Only three rates are published, so 10K
 * and sterling bill at the 14K rate and palladium at the platinum rate.
 */
export function ratePerGramForKarat(karatId: string): number {
  if (karatId === "gold18k") return METAL_RATE_PER_GRAM_USD["18k"];
  if (karatId === "platinum950" || karatId === "palladium950") return METAL_RATE_PER_GRAM_USD.platinum;
  return METAL_RATE_PER_GRAM_USD["14k"]; // gold10k / gold14k / silver925
}

export interface RingPriceBreakdown {
  metalUSD: number;
  paveUSD: number;
  laborUSD: number;
  complexityUSD: number;
  totalUSD: number;
}

const ZERO_BREAKDOWN: RingPriceBreakdown = {
  metalUSD: 0,
  paveUSD: 0,
  laborUSD: 0,
  complexityUSD: 0,
  totalUSD: 0,
};

function sideStoneCount(state: RingState): number {
  const layout = state.engagement?.sideStones.layout;
  return layout === "three" ? 2 : layout === "two" ? 1 : 0;
}

/** Centre + side stones in carats — priced as loose stones, i.e. NOT here. */
function centreAndSideCarat(state: RingState): number {
  const e = state.engagement;
  if (!e?.enabled) return 0;
  const centre = e.centerStone.caratCt;
  return centre + sideStoneCount(state) * centre * e.sideStones.caratRatio;
}

/**
 * Setting-only estimate: metal weight x karat rate + melee carats x $/ct (x the
 * run's origin/colour/clarity factor) + labor (base + per stone) + head/layout
 * complexity bumps, all x retail margin.
 *
 * `RingSpec.weightGrams` already folds the head volume in, and `totalCarat`
 * already folds the centre + side stones in — so the melee carat is
 * `totalCarat` minus those, and the centre stone is never priced as pavé.
 */
export function estimateRingPrice(state: RingState): RingPriceBreakdown {
  const spec = state.spec;
  if (!spec) return ZERO_BREAKDOWN;

  const metalUSD = spec.weightGrams * ratePerGramForKarat(state.metalKarat);
  const meleeCt = Math.max(0, spec.totalCarat - centreAndSideCarat(state));
  const paveUSD = meleeCt * PAVE_RATE_PER_CT_USD * meleeQualityFactor(state.diamond);
  const laborUSD = LABOR_BASE_USD + spec.stoneCount * LABOR_PER_STONE_USD;

  const e = state.engagement;
  const complexityUSD = e?.enabled
    ? (HEAD_COMPLEXITY_BUMP_USD[e.head.understructure] ?? 0) +
      sideStoneCount(state) * MULTI_STONE_SIDE_BUMP_USD
    : 0;

  const totalUSD = (metalUSD + paveUSD + laborUSD + complexityUSD) * RETAIL_MARGIN;
  return { metalUSD, paveUSD, laborUSD, complexityUSD, totalUSD };
}

export function formatUSD(n: number): string {
  return n.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });
}

// ─── Warnings ───────────────────────────────────────────────────────────────

const DISMISSED_KEY = "ring_dismissed_warnings";

function readDismissed(): Set<string> {
  try {
    const raw = sessionStorage.getItem(DISMISSED_KEY);
    const list = raw ? (JSON.parse(raw) as string[]) : [];
    return new Set(Array.isArray(list) ? list : []);
  } catch {
    return new Set();
  }
}

function persistDismissed(set: Set<string>) {
  try {
    sessionStorage.setItem(DISMISSED_KEY, JSON.stringify([...set]));
  } catch {
    /* sessionStorage unavailable — dismissal just won't stick, non-fatal */
  }
}

/** Manufacturability validator output (`RingSpec.warnings`) as dismissible
 * amber chips. Dismissal is per tab session, keyed by the warning text — a
 * reload brings back any warning that is still live. */
export function WarningChips({ warnings }: { warnings: string[] }) {
  const [dismissed, setDismissed] = useState<Set<string>>(readDismissed);
  const visible = warnings.filter((w) => !dismissed.has(w));

  if (visible.length === 0) return null;

  const dismiss = (w: string) => {
    setDismissed((prev) => {
      const next = new Set(prev).add(w);
      persistDismissed(next);
      return next;
    });
  };

  return (
    <div
      data-testid="engagement-warnings"
      className="flex flex-wrap gap-1.5 border-t border-outline-variant/40 bg-surface-container-high/30 px-6 pb-0 pt-3"
    >
      <AnimatePresence initial={false}>
        {visible.map((w) => (
          <motion.button
            key={w}
            type="button"
            layout
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            transition={{ duration: 0.16, ease: EASE_OUT }}
            onClick={() => dismiss(w)}
            title="Dismiss for this session"
            className="mb-3 flex max-w-full items-center gap-1.5 rounded-full border border-amber-300/70 bg-amber-50 px-2.5 py-1 text-left text-amber-800 transition-colors hover:border-amber-400"
          >
            <Icon name="warning" size={13} className="shrink-0 text-amber-600" />
            <span className="truncate font-body text-[10px] leading-tight">{w}</span>
            <Icon name="close" size={12} className="shrink-0 text-amber-500" />
          </motion.button>
        ))}
      </AnimatePresence>
    </div>
  );
}

// ─── Spec panel ─────────────────────────────────────────────────────────────

export interface SpecPanelProps {
  /** Which ring the builder is editing (`setActiveRing`). */
  activeRing?: number;
  /** Rings in the set, ≥ 1. Drives the per-ring price rows and the total. */
  ringCount?: number;
  className?: string;
}

/** The price rows follow the names the shopper actually gave their rings — a fourth
 * ring, or a renamed one, would otherwise still print "Her"/"His". First word only,
 * because the row is 32 px wide. */
function priceRowLabel(rows: RingRow[], i: number): string {
  return ringLabel(rows, i).split(/\s+/)[0];
}

function karatLabel(catalogs: Catalogs, id: string): string {
  return catalogs.metalKarats.find((k) => k.id === id)?.label ?? id;
}

function metalLabel(catalogs: Catalogs, presetId: string): string {
  return catalogs.metalPresets.find((m) => m.id === presetId)?.label ?? presetId;
}

/** "cushion-elongated" → "Cushion Elongated". Covers all 12 `CenterStoneShape`
 * members without a lookup table (the engagement `SHAPES` table missed two). */
function shapeLabel(shape: string): string {
  return shape.split("-").map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");
}

interface RingPrice {
  totalUSD: number;
  detail: string;
}

/**
 * Live spec + price footer. Self-sufficient (reads `useRing()`); the two
 * optional props only exist so the top bar can tell it which ring is being
 * edited — the viewer exposes one `RingState` at a time (`setActiveRing`
 * switches it), so the other ring's figures come from the last time it was
 * selected.
 */
export function SpecPanel({ activeRing = 0, ringCount = 1, className = "" }: SpecPanelProps) {
  const { state, catalogs, engagementOn } = useRing();
  const set = useRingSet();
  const spec = state.spec;

  const metals = state.materials.map((m) => metalLabel(catalogs, m.presetId)).join(" + ");
  const karat = karatLabel(catalogs, state.metalKarat);
  const price = estimateRingPrice(state);

  // The viewer only ever hands back the ACTIVE ring, so the inactive ring's
  // price is whatever it was when it was last selected. Geometry can't change
  // while a ring is deselected, so the cached figure stays correct.
  const cache = useRef<Record<number, RingPrice>>({}).current;
  if (spec) cache[activeRing] = { totalUSD: price.totalUSD, detail: `${karat} · ${spec.weightGrams.toFixed(2)} g` };
  // Drop rings that have since been removed, or their price would still be in
  // the total.
  for (const key of Object.keys(cache)) if (Number(key) >= Math.max(1, ringCount)) delete cache[Number(key)];

  const rings = Array.from({ length: Math.max(1, ringCount) }, (_, i) => i);
  const grandTotal = rings.reduce((sum, i) => sum + (cache[i]?.totalUSD ?? 0), 0);

  // Collapsed by default: weight, metal and the price rows are what you watch
  // while designing; the full figures are one click away (and in the spec sheet),
  // and keeping them folded leaves the tab body the room it needs.
  const [detailOpen, setDetailOpen] = useState(false);

  const centre = engagementOn ? state.engagement?.centerStone : undefined;
  const mm = spec?.centerStoneMM;

  const warnCount = spec?.warnings?.length ?? 0;

  return (
    // One line by default. Measured on the live rail: this panel stood 188 px
    // tall against a 226 px tab body, so the readout had almost as much room as
    // the controls it describes. Collapsed it is ~44 px and still carries the
    // three figures you actually watch while designing — weight, metal, total —
    // with everything else one click away (and in the spec sheet).
    <div className={`shrink-0 ${className}`}>
      <button
        onClick={() => setDetailOpen((v) => !v)}
        aria-expanded={detailOpen}
        aria-label={detailOpen ? "Hide specification" : "Show specification"}
        className="flex w-full items-center gap-2.5 border-t border-outline-variant/40 bg-surface-container-high/30 px-6 py-2.5 text-left"
      >
        <Icon name={detailOpen ? "expand_more" : "expand_less"} size={16} />
        <span className="shrink-0 font-body text-[12px] tabular-nums text-primary">
          {spec ? `${spec.weightGrams.toFixed(2)} g` : "—"}
        </span>
        <span className="min-w-0 flex-1 truncate font-body text-[11px] text-on-surface-variant">
          {karat} · {metals}
        </span>
        {warnCount > 0 && !detailOpen ? (
          // Collapsed, the warnings still have to announce themselves — a count
          // chip, expanded into the real dismissible list below.
          <span
            data-testid="spec-warning-count"
            className="shrink-0 rounded-full bg-tertiary-container px-1.5 py-0.5 font-body text-[10px] font-semibold text-on-tertiary-container"
          >
            {warnCount}
          </span>
        ) : null}
        <span
          data-testid="spec-price-total"
          className="shrink-0 font-body text-[13px] font-semibold tabular-nums text-primary"
        >
          {spec ? formatUSD(grandTotal) : "—"}
        </span>
      </button>

      {detailOpen ? (
        <div className="max-h-[40vh] overflow-y-auto">
          {spec?.warnings?.length ? <WarningChips warnings={spec.warnings} /> : null}

          <div className="border-t border-outline-variant/40 bg-surface-container-high/30 px-6 py-4">
            <div className="mb-3 flex items-end justify-between">
              <div className="flex flex-col">
                <span className="font-body text-[10px] uppercase tracking-[0.1em] text-outline">Weight</span>
                <span className="font-display text-headline-md leading-none text-primary">
                  {spec ? spec.weightGrams.toFixed(2) : "—"}
                  <span className="ml-1 text-[12px] text-on-surface-variant">g</span>
                </span>
              </div>
              <div className="text-right">
                <span className="block font-body text-[10px] uppercase tracking-[0.1em] text-outline">Metal</span>
                <span className="font-body text-[12px] text-primary">{metals}</span>
                <span className="block font-body text-[11px] text-on-surface-variant">{karat}</span>
              </div>
            </div>

            {centre ? (
              <div className="mb-3 border-t border-outline-variant/30 pt-3">
                <span className="mb-1 block font-body text-[10px] uppercase tracking-[0.1em] text-outline">
                  Center stone
                </span>
                <div data-testid="spec-center-stone" className="mb-2 flex items-end justify-between">
                  <span className="font-display text-headline-md leading-none text-primary">
                    {shapeLabel(centre.shape)}
                  </span>
                  <span className="font-body text-[13px] text-on-surface-variant">
                    {centre.caratCt.toFixed(2)} ct
                  </span>
                </div>
                <Row
                  label="Dimensions"
                  value={mm ? `${mm.l.toFixed(2)} × ${mm.w.toFixed(2)} × ${mm.d.toFixed(2)} mm` : "Pending"}
                />
                {spec?.headVolumeMM3 !== undefined ? (
                  <Row label="Head volume" value={`${spec.headVolumeMM3.toFixed(1)} mm³`} />
                ) : null}
              </div>
            ) : null}

            <div className="grid grid-cols-2 gap-x-5 gap-y-1.5">
              <Row label="Volume" value={spec ? `${spec.metalVolumeMM3.toFixed(1)} mm³` : "Pending"} />
              <Row label="Surface" value={spec ? `${spec.surfaceAreaMM2.toFixed(0)} mm²` : "Pending"} />
              <Row label="Ring size" value={`US ${state.ringSizeUS} / EU ${state.ringSizeEU}`} />
              <Row label="Inner Ø" value={spec ? `${spec.innerDiameterMM.toFixed(2)} mm` : "Pending"} />
              <Row label="Width" value={`${state.widthMM.toFixed(2)} mm`} />
              <Row label="Thickness" value={`${state.thicknessMM.toFixed(2)} mm`} />
              <Row label="Circumference" value={spec ? `${spec.circumferenceMM.toFixed(1)} mm` : "Pending"} />
              <Row label="Stones" value={spec && spec.stoneCount > 0 ? String(spec.stoneCount) : "—"} />
              {spec && spec.stoneCount > 0 ? (
                <>
                  <Row label="Per stone" value={`${spec.perStoneCarat.toFixed(3)} ct`} />
                  <Row label="Total carat" value={`${spec.totalCarat.toFixed(3)} ct`} />
                </>
              ) : null}
            </div>

            <div
              data-testid="spec-price-bar"
              className="mt-3 rounded-lg border border-outline-variant/50 bg-surface-container-lowest px-3 py-2.5"
            >
              {rings.map((i) => (
                <div key={i} className="flex items-baseline gap-2.5 py-[3px]">
                  <span className="w-8 shrink-0 font-body text-[10px] font-semibold uppercase tracking-[0.08em] text-on-surface-variant">
                    {priceRowLabel(set.rows, i)}
                  </span>
                  <span className="flex-1 truncate font-body text-[10px] text-outline">
                    {cache[i]?.detail ?? "Not yet priced — select this ring"}
                  </span>
                  <span className="font-body text-[12px] tabular-nums text-on-surface">
                    {cache[i] ? formatUSD(cache[i].totalUSD) : "—"}
                  </span>
                </div>
              ))}

              <div className="mt-1.5 flex items-baseline justify-between border-t border-outline-variant/40 pt-2">
                <div className="flex flex-col">
                  <span className="font-body text-[10px] uppercase tracking-[0.12em] text-outline">Total</span>
                  <span className="font-body text-[10px] text-outline">(setting only) — Live estimate</span>
                </div>
                <div className="flex items-baseline gap-2">
                  <span className="font-display text-headline-md leading-none text-primary">
                    {spec ? formatUSD(grandTotal) : "—"}
                  </span>
                  <span className="font-body text-[10px] uppercase tracking-[0.08em] text-outline">MSRP</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline justify-between gap-2">
      <span className="font-body text-[11px] text-on-surface-variant">{label}</span>
      <span className="font-body text-[12px] tabular-nums text-primary">{value}</span>
    </div>
  );
}
